<template>
  <section class="saved-locations">
    <div class="saved-locations__heading">
      <div class="saved-locations__title-row">
        <h2 class="header-secondary__title">Taggable Accounts</h2>
        <InfoIconWithTooltip :width="280">These accounts can be used in image Tags and caption Mentions for your social posts.</InfoIconWithTooltip>
        <!--<p>Business and locations to highlight in your posts.</p>-->
      </div>
      <SrpButton fill="outlined" size="small" color="teal" @click="openCreate"
        ><template #icon><IconEmbedded name="plus_3" :size="18" /></template>Add Account</SrpButton
      >
    </div>
    <Loader v-if="isLoading" />
    <div v-else-if="visibleLocations.length" class="saved-locations__list">
      <div v-for="location in visibleLocations" :key="location.id" class="saved-locations__row">
        <LocationAvatar :src="location.icon" :name="location.name" :size="46" />
        <div class="saved-locations__copy">
          <div class="saved-locations__name-row">
            <strong>{{ location.name }}</strong>
            <TooltipWrapper v-if="location.website?.trim()" text="Website">
              <template #hoverableElement>
                <a class="saved-locations__website-link" :href="websiteUrl(location)" target="_blank" rel="noopener noreferrer" aria-label="Website" @click.stop>
                  <IconEmbedded name="external-link_2-5" :size="14" color="#667085" />
                </a>
              </template>
            </TooltipWrapper>
          </div>
          <div class="saved-locations__meta">
            <LocationSocialHandles :location="location" />
            <template v-if="location.googlePlaceId">
              <span v-if="hasSocialHandles(location)" class="saved-locations__sep"> · </span>
              <TooltipWrapper text="Location set">
                <template #hoverableElement>
                  <a class="saved-locations__place-link" :href="googlePlaceUrl(location)" target="_blank" rel="noopener noreferrer" @click.stop>
                    <IconEmbedded name="map-pin_2" :size="14" color="#667085" />
                  </a>
                </template>
              </TooltipWrapper>
            </template>
          </div>
        </div>
        <button type="button" @click="openEdit(location)">Edit</button>
        <button type="button" class="saved-locations__archive" @click="archive(location)">Archive</button>
      </div>
    </div>
    <!--<button v-else type="button" class="saved-locations__empty" @click="openCreate">Add your first saved Location</button>-->
    <LocationEditorModal v-model:isVisible="isEditorVisible" :customerId="props.customerId" :location="locationToEdit" @saved="handleSaved" />
    <SrpModal v-model:isVisible="isArchiveModalVisible" size="small">
      <template #header>
        <h2 class="global-h2">Archive Account</h2>
      </template>
      <template #content>
        <p class="global-p">Archive {{ locationToArchive?.name }}?<br />Existing posts will retain their saved reference.</p>
      </template>
      <template #footer="{ closeModal }">
        <SrpButton color="gray" fill="outlined" :isDisabled="isArchiving" @click="closeModal">Cancel</SrpButton>
        <SrpButton color="teal" :isDisabled="isArchiving" @click="confirmArchive">Archive</SrpButton>
      </template>
    </SrpModal>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import TooltipWrapper from "@components/ui/TooltipWrapper.vue";
import Loader from "@components/Loader/Loader.vue";
import LocationAvatar from "./LocationAvatar.vue";
import LocationSocialHandles from "./LocationSocialHandles.vue";
import LocationEditorModal from "./LocationEditorModal.vue";
import SocialLocationsRepo from "@repos/SocialLocationsRepo";
import type { LocationData } from "@contracts/socialLocations";

const props = withDefaults(
  defineProps<{
    customerId: string;
    showAllLocations?: boolean;
  }>(),
  { showAllLocations: false }
);
const locations = ref<LocationData[]>([]);
const visibleLocations = computed(() => (props.showAllLocations ? locations.value : locations.value.filter(l => l.origin === "Manual")));
const isLoading = ref(false);
const isEditorVisible = ref(false);
const locationToEdit = ref<LocationData | null>(null);
const isArchiveModalVisible = ref(false);
const locationToArchive = ref<LocationData | null>(null);
const isArchiving = ref(false);

onMounted(load);
async function load() {
  isLoading.value = true;
  try {
    locations.value = await SocialLocationsRepo.list(props.customerId, false, false);
  } finally {
    isLoading.value = false;
  }
}
function openCreate() {
  locationToEdit.value = null;
  isEditorVisible.value = true;
}
function openEdit(location: LocationData) {
  locationToEdit.value = location;
  isEditorVisible.value = true;
}
function archive(location: LocationData) {
  locationToArchive.value = location;
  isArchiveModalVisible.value = true;
}
async function confirmArchive() {
  if (!locationToArchive.value || isArchiving.value) return;
  isArchiving.value = true;
  try {
    await SocialLocationsRepo.archive(props.customerId, locationToArchive.value.id);
    isArchiveModalVisible.value = false;
    locationToArchive.value = null;
    await load();
  } finally {
    isArchiving.value = false;
  }
}
async function handleSaved() {
  await load();
}

function websiteUrl(location: LocationData): string {
  const website = location.website?.trim() ?? "";
  if (/^https?:\/\//i.test(website)) return website;
  return `https://${website}`;
}

function googlePlaceUrl(location: LocationData): string {
  let uri = "https://www.google.com/maps/search/?api=1";
  if (location.latitude != null && location.longitude != null) {
    uri += `&query=${location.latitude},${location.longitude}`;
  } else {
    uri += `&query=${encodeURIComponent(location.name)}`;
  }
  if (location.googlePlaceId) {
    uri += `&query_place_id=${encodeURIComponent(location.googlePlaceId)}`;
  }
  return uri;
}

function hasSocialHandles(location: LocationData): boolean {
  const socialSet = location.socialSet;
  return Boolean(socialSet?.instagramHandle || socialSet?.facebookPageName || socialSet?.tikTokHandle);
}
</script>

<style scoped lang="scss">
.saved-locations {
  margin-bottom: 30px;
}
.saved-locations__heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  margin-bottom: 12px;
}
.saved-locations__title-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.saved-locations__heading h2,
.saved-locations__heading p {
  margin: 0;
}
.saved-locations__heading p {
  margin-top: 4px;
  color: #667085;
}
.saved-locations__list {
  border: 1px solid #d8dce2;
  border-radius: 9px;
  overflow: hidden;
}
.saved-locations__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #e7e9ed;
}
.saved-locations__row:last-child {
  border-bottom: 0;
}
.saved-locations__copy {
  flex: 1;
  display: grid;
  gap: 3px;
}
.saved-locations__name-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.saved-locations__name-row :deep(.tooltip-wrapper),
.saved-locations__name-row :deep(.tooltip-wrapper__hoverable-element) {
  display: inline-flex;
  align-items: center;
}
.saved-locations__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 0;
}
.saved-locations__meta :deep(.tooltip-wrapper) {
  display: inline-flex;
  align-items: center;
}
.saved-locations__meta :deep(.tooltip-wrapper__hoverable-element) {
  display: inline-flex;
  align-items: center;
}
.saved-locations__sep {
  margin: 0 4px;
  color: #667085;
  font-size: 90%;
}
.saved-locations__place-link,
.saved-locations__website-link {
  display: inline-flex;
  align-items: center;
  position: relative;
  color: inherit;
  text-decoration: none;
}
.saved-locations__place-link:hover,
.saved-locations__website-link:hover {
  opacity: 0.75;
}
.saved-locations__row button {
  border: 0;
  background: transparent;
  color: #087f82;
  cursor: pointer;
}
.saved-locations__row .saved-locations__archive {
  color: #806468;
}
.saved-locations__empty {
  width: 100%;
  padding: 18px;
  border: 1px dashed #9aa5b1;
  border-radius: 8px;
  background: #fbfdfd;
  color: #087f82;
  cursor: pointer;
}
</style>
